import type { SlideConfig, WrappedData, WrappedTotalsData } from '../types/wrapped'
import type { SlowConnectionInfo } from '../hooks/useSlowConnectionDetection'

/** Default timeout in ms before falling back from video to image background */
export const VIDEO_FALLBACK_TIMEOUT_MS = 2000

/** Shorter timeout for mobile devices where video performance is more constrained */
export const VIDEO_FALLBACK_TIMEOUT_MOBILE_MS = 1000

/** Very short timeout when reduced motion is preferred (proxy for iOS Low Power Mode) */
export const VIDEO_FALLBACK_TIMEOUT_REDUCED_MOTION_MS = 500

/**
 * Check if device is mobile based on screen width
 * Called at runtime to avoid SSR issues
 */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

/**
 * Get the video fallback timeout based on connection quality and device preferences.
 * Returns shorter timeouts for slow connections to improve perceived performance.
 * Returns 0 (skip video entirely) when Data Saver is enabled.
 *
 * Priority:
 * 1. Data Saver enabled → skip video (0ms)
 * 2. Very slow connection (slow-2g) → skip video (0ms)
 * 3. Prefers reduced motion → very short timeout (500ms) - acts as proxy for iOS Low Power Mode
 * 4. Slow connection (2g) → very short timeout (500ms)
 * 5. Medium connection (3g) → short timeout (1000ms)
 * 6. Mobile device → shorter timeout (1000ms)
 * 7. Default → standard timeout (2000ms)
 *
 * @param connectionInfo - Connection information from useSlowConnectionDetection hook
 * @returns Timeout in milliseconds, or 0 to skip video entirely
 */
export function getVideoFallbackTimeout(connectionInfo?: SlowConnectionInfo): number {
  if (!connectionInfo) {
    // No connection info available - use mobile-aware default
    return isMobileDevice() ? VIDEO_FALLBACK_TIMEOUT_MOBILE_MS : VIDEO_FALLBACK_TIMEOUT_MS
  }

  // Skip video entirely if Data Saver is enabled - user explicitly wants to save data
  if (connectionInfo.saveData) return 0

  // Skip video entirely on very slow connections
  if (connectionInfo.effectiveType === 'slow-2g') return 0

  // If user prefers reduced motion, use very short timeout
  // This often correlates with iOS Low Power Mode
  if (connectionInfo.prefersReducedMotion) return VIDEO_FALLBACK_TIMEOUT_REDUCED_MOTION_MS

  // Use shorter timeouts for slower connections
  switch (connectionInfo.effectiveType) {
    case '2g':
      return 500 // Very short timeout on 2G
    case '3g':
      return VIDEO_FALLBACK_TIMEOUT_MOBILE_MS // Short timeout on 3G
    case '4g':
    default:
      // Even on good connections, use shorter timeout on mobile
      return isMobileDevice() ? VIDEO_FALLBACK_TIMEOUT_MOBILE_MS : VIDEO_FALLBACK_TIMEOUT_MS
  }
}

export const WRAPPED_SLIDES: Array<SlideConfig> = [
  {
    id: 'intro',
    type: 'intro',
    duration: 12000,
    video: {
      id: '1152033796',
      aspectRatio: '9:16',
    },
    fallbackImageId: 'wrapped-intro-fallback',
  },
  {
    id: 'personality',
    type: 'personality',
    duration: 9000,
  },
  {
    id: 'destinations',
    type: 'stat',
    duration: 8000,
  },
  {
    id: 'nights',
    type: 'stat',
    duration: 7000,
    video: {
      id: '1151421758',
      aspectRatio: '9:16',
    },
    fallbackImageId: 'wrapped-spent-fallback',
  },
  {
    id: 'distance',
    type: 'stat',
    duration: 7000,
    video: {
      id: '1151431656',
      aspectRatio: '9:16',
    },
    fallbackImageId: 'wrapped-distance-fallback',
  },
  {
    id: 'top-destinations-quiz',
    type: 'quiz',
    duration: 7000,
  },
  {
    id: 'offer-views',
    type: 'stat',
    duration: 7000,
    video: {
      id: '1151421744',
      aspectRatio: '9:16',
    },
    fallbackImageId: 'wrapped-offers-fallback',
  },
  {
    id: 'top-offer',
    type: 'offer',
    duration: 9000,
  },
  {
    id: 'savings',
    type: 'stat',
    duration: 7000,
    video: {
      id: '1151421772',
      aspectRatio: '9:16',
    },
    fallbackImageId: 'wrapped-savings-fallback-2',
  },
  {
    id: 'loyalty',
    type: 'loyalty',
    duration: 9000,
  },
  {
    id: 'summary',
    type: 'summary',
    duration: 11000,
  },
  {
    id: 'prediction',
    type: 'prediction',
    duration: 9000,
    video: {
      id: '1151421780',
      aspectRatio: '9:16',
    },
    fallbackImageId: 'wrapped-prediction-fallback',
  },
]

/**
 * Returns the filtered slides based on user data conditions
 */
export function getActiveSlides(data: WrappedData): Array<SlideConfig> {
  return WRAPPED_SLIDES.filter((slide) => {
    switch (slide.id) {
      case 'top-destinations-quiz':
        return data.topDestinations.length >= 3
      case 'loyalty':
        return !!data.loyalty?.points || data.loyalty?.statusCredits
      case 'top-offer':
        return !!data.topOffer?.imageId?.length
      default:
        return true
    }
  })
}

/**
 * Returns the filtered slides for totals-only mode (when user has no wrapped data)
 * Excludes slides that require user-specific data like personality, loyalty, etc.
 */
export function getActiveSlidesForTotalsOnly(data: WrappedTotalsData): Array<SlideConfig> {
  // Slides to skip in totals-only mode (require user-specific data)
  const skipSlides = new Set<string>(['personality', 'loyalty', 'summary'])

  return WRAPPED_SLIDES.filter((slide) => {
    // Skip slides that require user-specific data
    if (skipSlides.has(slide.id)) {
      return false
    }

    switch (slide.id) {
      case 'top-destinations-quiz':
        return data.topDestinations.length >= 3
      case 'top-offer':
        return !!data.topOffer?.imageId?.length
      default:
        return true
    }
  })
}
